import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, User, Mail, Phone, ShoppingBag, DollarSign, 
  Users, Edit2, Save, X, Package, TrendingUp, Calendar,
  CreditCard, CheckCircle, AlertTriangle, ExternalLink, Store, Heart, AlertCircle, RefreshCcw, Gift
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { safeGetJSON, safeSetJSON } from '@/utils/safeStorage';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import UpgradeToCreatorModal from '@/components/UpgradeToCreatorModal';
import TwoFactorAuth from '@/components/TwoFactorAuth';
import EmailUpdatesSubscribe from '@/components/EmailUpdatesSubscribe';
import AffiliateTourModal from '@/components/AffiliateTourModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function BuyerProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [affiliateStripeStatus, setAffiliateStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAffiliateTour, setShowAffiliateTour] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    loadProfile();
    checkAffiliateStripeStatus();

    // Check for Stripe return parameters
    if (searchParams.get('affiliate_stripe_connected') === 'true') {
      toast.success('Stripe account connected! You can now receive instant affiliate payouts.');
      checkAffiliateStripeStatus();
    } else if (searchParams.get('stripe_refresh') === 'true') {
      toast.info('Please complete your Stripe onboarding');
    }
  }, [searchParams]);

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(res.data);
      setFormData({ name: res.data.name || '', phone: res.data.phone || '' });
    } catch (error) {
      // Don't toast a generic "Failed" if the user just isn't logged in
      // (the page will redirect via PrivateRoute anyway). Only surface
      // toasts on real errors.
      if (error.response?.status && error.response.status !== 401) {
        toast.error('Failed to load profile');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const checkAffiliateStripeStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/affiliate/stripe/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAffiliateStripeStatus(res.data);
    } catch (err) {
      console.debug('Affiliate Stripe status check failed (non-fatal):', err?.message || err);
    }
  };

  const handleAffiliateStripeOnboard = async () => {
    setStripeLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/affiliate/stripe/onboard`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.status === 'complete') {
        toast.success('Stripe already connected!');
        checkAffiliateStripeStatus();
      } else if (res.data.onboarding_url) {
        window.location.href = res.data.onboarding_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start Stripe onboarding');
    } finally {
      setStripeLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/profile/me`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated successfully');
      setEditing(false);
      loadProfile();
      
      // Update localStorage user data
      const storedUser = safeGetJSON('user', {});
      storedUser.name = formData.name;
      safeSetJSON('user', storedUser);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00F0FF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-neutral-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {t('profile.backToMarketplace')}
            </Button>
          </div>
          <h1 className="text-xl font-bold">{t('profile.title')}</h1>
          <div className="w-32"></div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        {/* Profile Card */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8 mb-8">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-gradient-to-br from-[#00F0FF] to-[#00FF94] rounded-full flex items-center justify-center">
                <User className="w-10 h-10 text-black" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{profile?.name || 'User'}</h2>
                <p className="text-neutral-400">{profile?.email}</p>
                {profile?.is_creator && (
                  <span className="inline-block mt-2 px-3 py-1 bg-[#00F0FF]/20 text-[#00F0FF] text-sm rounded-full">
                    {t('profile.creator')}
                  </span>
                )}
              </div>
            </div>
            {!editing ? (
              <Button 
                variant="outline" 
                onClick={() => setEditing(true)}
                className="border-white/20"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                {t('profile.editProfile')}
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setEditing(false);
                    setFormData({ name: profile?.name || '', phone: profile?.phone || '' });
                  }}
                  className="border-white/20"
                >
                  <X className="w-4 h-4 mr-2" />
                  {t('profile.cancel')}
                </Button>
                <Button 
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? t('profile.saving') : t('profile.save')}
                </Button>
              </div>
            )}
          </div>

          {/* Profile Fields */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm text-neutral-400 mb-2">
                <User className="w-4 h-4 inline mr-2" />
                {t('profile.name')}
              </label>
              {editing ? (
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-neutral-800 border-white/10"
                  placeholder={t('profile.name')}
                />
              ) : (
                <p className="text-lg">{profile?.name || t('profile.notSet')}</p>
              )}
            </div>
            <div>
              <label className="block text-sm text-neutral-400 mb-2">
                <Mail className="w-4 h-4 inline mr-2" />
                {t('profile.email')}
              </label>
              <p className="text-lg">{profile?.email}</p>
            </div>
            <div>
              <label className="block text-sm text-neutral-400 mb-2">
                <Phone className="w-4 h-4 inline mr-2" />
                {t('profile.phone')}
              </label>
              {editing ? (
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-neutral-800 border-white/10"
                  placeholder={t('profile.phone')}
                />
              ) : (
                <p className="text-lg">{profile?.phone || t('profile.notSet')}</p>
              )}
            </div>
            <div>
              <label className="block text-sm text-neutral-400 mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                Member Since
              </label>
              <p className="text-lg">
                {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Two-Factor Authentication */}
        <div className="mb-8">
          <TwoFactorAuth />
        </div>

        {/* Email Updates Subscription */}
        <div className="mb-8">
          <EmailUpdatesSubscribe />
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <button
            onClick={() => navigate('/wishlist')}
            className="p-4 bg-neutral-900/50 border border-white/10 rounded-xl hover:border-[#00F0FF]/30 transition-all text-center"
          >
            <Heart className="w-6 h-6 text-red-400 mx-auto mb-2" />
            <span className="text-sm font-medium">Wishlist</span>
          </button>
          <button
            onClick={() => navigate('/my-purchases')}
            className="p-4 bg-neutral-900/50 border border-white/10 rounded-xl hover:border-[#00F0FF]/30 transition-all text-center"
          >
            <Package className="w-6 h-6 text-[#00F0FF] mx-auto mb-2" />
            <span className="text-sm font-medium">My Purchases</span>
          </button>
          <button
            onClick={() => navigate('/affiliate/payouts')}
            className="p-4 bg-neutral-900/50 border border-white/10 rounded-xl hover:border-[#00F0FF]/30 transition-all text-center"
          >
            <DollarSign className="w-6 h-6 text-[#00F0FF] mx-auto mb-2" />
            <span className="text-sm font-medium">Affiliate Earnings</span>
          </button>
          <button
            onClick={() => navigate('/my-disputes')}
            className="p-4 bg-neutral-900/50 border border-white/10 rounded-xl hover:border-[#00F0FF]/30 transition-all text-center"
          >
            <AlertCircle className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
            <span className="text-sm font-medium">Disputes</span>
          </button>
          <button
            onClick={() => navigate('/my-subscriptions')}
            className="p-4 bg-neutral-900/50 border border-white/10 rounded-xl hover:border-[#00FF94]/30 transition-all text-center"
          >
            <RefreshCcw className="w-6 h-6 text-[#00FF94] mx-auto mb-2" />
            <span className="text-sm font-medium">Subscriptions</span>
          </button>
          <button
            onClick={() => navigate('/referrals')}
            className="p-4 bg-neutral-900/50 border border-white/10 rounded-xl hover:border-white/30 transition-all text-center"
          >
            <Gift className="w-6 h-6 text-white mx-auto mb-2" />
            <span className="text-sm font-medium">Referrals</span>
          </button>
        </div>

        {/* Become an Affiliate Banner — only show for users who haven't joined any programs yet */}
        {!profile?.stats?.affiliate_programs_joined && (
          <div
            className="relative bg-gradient-to-r from-[#00F0FF]/[0.08] via-black/40 to-[#00FF94]/[0.06] border border-[#00F0FF]/30 rounded-2xl p-5 md:p-6 mb-8 overflow-hidden"
            data-testid="become-affiliate-banner"
          >
            <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00F0FF]/60 to-transparent" />
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-[#00F0FF]/15 border border-[#00F0FF]/40 flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-7 h-7 text-[#00F0FF]" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#00FF94]/10 border border-[#00FF94]/40 text-[10px] font-bold uppercase tracking-wider text-[#00FF94]">
                    New
                  </span>
                  <h3 className="text-lg md:text-xl font-bold text-white">
                    Earn money by sharing AI tools you love
                  </h3>
                </div>
                <p className="text-sm text-neutral-400 leading-relaxed">
                  Get up to <span className="text-[#00FF94] font-semibold">30% commission</span> on every sale you refer. No application required — pick a tool, grab your link, share it.
                </p>
              </div>
              <div className="flex gap-2 w-full md:w-auto">
                <Button
                  onClick={() => setShowAffiliateTour(true)}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5 flex-1 md:flex-none"
                  data-testid="affiliate-tour-open"
                >
                  See how it works
                </Button>
                <Button
                  onClick={() => navigate('/affiliate/program')}
                  className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-bold flex-1 md:flex-none"
                  data-testid="affiliate-explore-btn"
                >
                  Explore tools
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Affiliate Stripe Connect Section */}
        {profile?.stats?.affiliate_programs_joined > 0 && (
          <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 mb-8">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-[#00F0FF]" />
              Affiliate Payout Setup
            </h3>
            
            {affiliateStripeStatus?.status === 'complete' ? (
              <div className="flex items-center gap-4 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                <CheckCircle className="w-8 h-8 text-green-400" />
                <div>
                  <h4 className="font-semibold text-green-400">Instant Payouts Enabled!</h4>
                  <p className="text-sm text-neutral-400">
                    When you make affiliate sales, commissions are automatically sent to your connected Stripe account.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                  <AlertTriangle className="w-8 h-8 text-yellow-400 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-yellow-400">Connect Stripe for Instant Payouts</h4>
                    <p className="text-sm text-neutral-400 mb-3">
                      Connect your Stripe account to receive affiliate commissions instantly when sales are made through your links. No more waiting for creators to manually pay you!
                    </p>
                    <Button
                      onClick={handleAffiliateStripeOnboard}
                      disabled={stripeLoading}
                      className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90"
                    >
                      {stripeLoading ? (
                        'Loading...'
                      ) : (
                        <>
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Connect Stripe Account
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                
                <div className="bg-neutral-800/50 rounded-xl p-4">
                  <h5 className="font-semibold text-sm mb-2">How Instant Payouts Work:</h5>
                  <ul className="text-sm text-neutral-400 space-y-1">
                    <li>• Someone clicks your affiliate link and makes a purchase</li>
                    <li>• Your commission is automatically calculated</li>
                    <li>• Payment is instantly transferred to your Stripe account</li>
                    <li>• No manual payout requests needed!</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-[#00F0FF]/20 rounded-lg">
                <ShoppingBag className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <span className="text-neutral-400 text-sm">Purchases</span>
            </div>
            <p className="text-3xl font-bold">{profile?.stats?.total_purchases || 0}</p>
          </div>
          
          <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-500" />
              </div>
              <span className="text-neutral-400 text-sm">Total Spent</span>
            </div>
            <p className="text-3xl font-bold">${(profile?.stats?.total_spent || 0).toFixed(2)}</p>
          </div>
          
          <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-[#00F0FF]/20 rounded-lg">
                <Users className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <span className="text-neutral-400 text-sm">Affiliate Programs</span>
            </div>
            <p className="text-3xl font-bold">{profile?.stats?.affiliate_programs_joined || 0}</p>
          </div>
          
          <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <TrendingUp className="w-5 h-5 text-yellow-500" />
              </div>
              <span className="text-neutral-400 text-sm">Affiliate Earnings</span>
            </div>
            <p className="text-3xl font-bold">${(profile?.stats?.affiliate_earnings || 0).toFixed(2)}</p>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid md:grid-cols-2 gap-4">
          <Button
            variant="outline"
            className="h-auto py-6 border-white/10 hover:bg-white/5 justify-start"
            onClick={() => navigate('/my-purchases')}
          >
            <Package className="w-6 h-6 mr-4 text-[#00F0FF]" />
            <div className="text-left">
              <p className="font-semibold">My Purchases</p>
              <p className="text-sm text-neutral-400">View your purchased products</p>
            </div>
          </Button>
          
          {profile?.is_creator ? (
            <Button
              variant="outline"
              className="h-auto py-6 border-white/10 hover:bg-white/5 justify-start"
              onClick={() => navigate('/dashboard')}
            >
              <TrendingUp className="w-6 h-6 mr-4 text-[#00F0FF]" />
              <div className="text-left">
                <p className="font-semibold">Creator Dashboard</p>
                <p className="text-sm text-neutral-400">Manage your store & products</p>
              </div>
            </Button>
          ) : (
            <Button
              variant="outline"
              className="h-auto py-6 border-white/10 hover:bg-white/5 justify-start"
              onClick={() => setShowUpgradeModal(true)}
            >
              <Store className="w-6 h-6 mr-4 text-[#00F0FF]" />
              <div className="text-left">
                <p className="font-semibold">{t('profile.becomeCreator')}</p>
                <p className="text-sm text-neutral-400">{t('profile.startSelling')}</p>
              </div>
            </Button>
          )}
        </div>
      </main>

      {/* Upgrade to Creator Modal */}
      <UpgradeToCreatorModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onSuccess={() => {
          loadProfile();
          navigate('/dashboard');
        }}
      />

      {/* Become an Affiliate quick tour */}
      <AffiliateTourModal
        open={showAffiliateTour}
        onClose={() => setShowAffiliateTour(false)}
      />

      {/* GDPR — self-serve account deletion. Lives at the bottom of the
          page (visually quiet, intentional friction so it isn't fired by
          mistake). The double-confirm + typed-DELETE backend guard add a
          second hurdle. */}
      <section className="container mx-auto px-4 md:px-12 max-w-4xl pb-16">
        <div className="rounded-2xl border border-red-500/20 bg-red-500/[0.03] p-6 md:p-7" data-testid="danger-zone-section">
          <h3 className="text-base font-bold text-red-400 mb-2 uppercase tracking-wider">Danger zone</h3>
          <p className="text-sm text-neutral-400 leading-relaxed mb-4">
            Permanently delete your theailedger.ai account and all personal data linked to it. Historical orders are kept for tax records but anonymized so they&apos;re no longer linked to you. <span className="text-red-400 font-semibold">This cannot be undone.</span>
          </p>
          <Button
            variant="outline"
            onClick={async () => {
              const first = window.prompt('Type DELETE to permanently delete your account.\n\nThis action cannot be undone.');
              if (first !== 'DELETE') {
                if (first !== null) toast.error('Cancelled — you must type DELETE exactly.');
                return;
              }
              const reason = window.prompt('Optional: tell us briefly why you\'re leaving (helps us improve). Leave blank to skip.') || '';
              try {
                const token = localStorage.getItem('token');
                await axios.delete('/me/account', {
                  data: { confirm: 'DELETE', reason },
                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                toast.success('Account deleted. Goodbye.');
                localStorage.clear();
                setTimeout(() => { window.location.href = '/'; }, 1500);
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to delete account. Contact support@theailedger.ai.');
              }
            }}
            className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
            data-testid="delete-account-btn"
          >
            Delete my account
          </Button>
        </div>
      </section>
    </div>
  );
}

export default BuyerProfile;
